import chapterOneScript from "../../../../docs/story-chapter-01-script-v2_1.md?raw";
import chapterTwoScript from "../../../../docs/story-chapter-02-script-v2_1.md?raw";
import chapterThreeScript from "../../../../docs/story-chapter-03-script-v2_1.md?raw";
import chapterFourScript from "../../../../docs/story-chapter-04-script-v2_1.md?raw";
import chapterFiveScript from "../../../../docs/story-chapter-05-script-v2_1.md?raw";
import chapterSixScript from "../../../../docs/story-chapter-06-script-v2_2.md?raw";
import extraOuterWindowScript from "../../../../docs/story-extra-outer-window-script-v1.md?raw";
import {
  CHAPTER_ONE_STORY,
  type ScenarioResultKey,
  type ScenarioStoryDefinition,
  type StoryBackdropDefinition,
  type StoryEntry,
  type StorySpeakerDefinition,
} from "../../shared/index.js";

interface ScriptScene {
  heading: string;
  entries: readonly StoryEntry[];
}

interface BackdropCue {
  pattern: RegExp;
  backdrop: string;
}

interface ChapterStoryPlan {
  chapterId: string;
  script: string;
  openingScenes: readonly string[];
  battleScenes: readonly string[];
  resultScenes: readonly string[];
  postscriptScenes: readonly string[];
  sceneBackdrops: Readonly<Record<string, string>>;
  cues: readonly BackdropCue[];
  selectedBranch?: string;
}

const BASE_SPEAKERS: Readonly<Record<string, StorySpeakerDefinition>> = Object.freeze({
  narrator: speaker("narrator", "語り", "", "", ""),
  protagonist: speaker("protagonist", "主人公", "新人のしもべ", "新", "protagonist"),
  manager: speaker("manager", "支配人", "かつての《羅針盤》", "針", "manager"),
  flint: speaker("flint", "《火打石》", "始まりを見つけるしもべ", "火", "flint"),
  ewer: speaker("ewer", "《水瓶》", "満たすことを愛するしもべ", "杯", "ewer"),
  projector: speaker("projector", "《映写機》", "反復を見つめるしもべ", "映", "projector"),
  frame: speaker("frame", "《額縁》", "記憶を切り取るしもべ", "額", "frame"),
  pin: speaker("pin", "《標本針》", "痕跡を留めるしもべ", "針", "pin"),
  man: speaker("man", "男", "犬の飼い主", "男", "man"),
  dog: speaker("dog", "犬", "老犬", "犬", "dog"),
  chapterTwoBoy: speaker("chapterTwoBoy", "少年", "遠足を待つ子供", "少", "chapterTwoBoy"),
  chapterTwoGirl: speaker("chapterTwoGirl", "少女", "慎重な冒険者", "少", "chapterTwoGirl"),
  chapterTwoMother: speaker("chapterTwoMother", "母親", "遠足の朝を見送る母親", "母", ""),
  chapterThreeChild: speaker("chapterThreeChild", "子供", "サンタを待つ少年", "子", "chapterThreeChild"),
  chapterThreeSanta: speaker("chapterThreeSanta", "サンタ", "迷える贈り手", "贈", "chapterThreeSanta"),
  chapterFourFriend: speaker("chapterFourFriend", "演奏者", "冬の広場の少女", "音", "chapterFourFriend"),
  chapterFourMemoryHeroine: speaker(
    "chapterFourMemoryHeroine",
    "記憶の主人公",
    "額の内側の少女",
    "記",
    "chapterFourMemoryHeroine",
  ),
  chapterFiveOwner: speaker("chapterFiveOwner", "持ち主", "ロボットと育った男", "主", "chapterFiveOwner"),
  chapterFiveRobot: speaker("chapterFiveRobot", "ロボット", "古い家庭用機体", "機", "chapterFiveRobot"),
});

const SPEAKER_ALIASES: Readonly<Record<string, string>> = Object.freeze({
  主人公: "protagonist",
  支配人: "manager",
  "支配人 / 《羅針盤》": "manager",
  羅針盤: "manager",
  "《羅針盤》": "manager",
  "《火打石》": "flint",
  "《水瓶》": "ewer",
  "《映写機》": "projector",
  "《額縁》": "frame",
  "《標本針》": "pin",
  選択した先輩: "flint",
  男: "man",
  犬: "dog",
  少年: "chapterTwoBoy",
  少女: "chapterTwoGirl",
  母親: "chapterTwoMother",
  子供: "chapterThreeChild",
  サンタ: "chapterThreeSanta",
  父親: "chapterThreeSanta",
  演奏者: "chapterFourFriend",
  親友: "chapterFourFriend",
  ベンチの少女: "chapterFourMemoryHeroine",
  記憶の主人公: "chapterFourMemoryHeroine",
  持ち主: "chapterFiveOwner",
  ロボット: "chapterFiveRobot",
});

const BACKDROPS: Readonly<Record<string, StoryBackdropDefinition>> = createBackdrops([
  ["dark", "暗転", "dark"],
  ["backstage", "古い緞帳の舞台裏", "scene"],
  ["theater", "夢の劇場", "theater"],
  ["nightEyes", "《夜の目》の客席", "night-eyes"],
  ["windowActive", "夢へ開いた《窓》", "window-active"],
  ["dreamStreet", "帰らずの散歩道", "scene"],
  ["loopingWalkStill", "繰り返す夕暮れの散歩", "scene"],
  ["ballReturnStill", "帰ってきた赤いボール", "scene"],
  ["walkingHomeStill", "家の明かりへ帰る道", "scene"],
  ["morningBedroom", "男と犬の朝", "scene", true],
  ["morningStill", "鼻先が触れた朝", "scene", true],
  ["projectionRoom", "小さな映写室", "scene"],
  ["hospitalRoom", "病室", "scene"],
  ["selectionTable", "六枚の演目札", "scene"],
  ["outerWindow", "もう一つの《窓》", "scene"],
  ["chapterTwoMountain", "竜の待つ山", "scene"],
  ["chapterTwoSummit", "山頂のドラゴン", "scene"],
  ["chapterTwoFlight", "ドラゴンの背の空", "scene"],
  ["chapterTwoMorning", "遠足の朝", "scene", true],
  ["chapterThreeCorridor", "終わらない廊下", "scene"],
  ["chapterThreeField", "雪の家", "scene"],
  ["chapterThreeFireplace", "暖炉の前", "scene"],
  ["chapterThreeMorningRoom", "クリスマスの朝", "scene"],
  ["chapterThreeNightmare", "映写された悪夢", "scene"],
  ["chapterThreeCocoa", "二人分のココア", "scene"],
  ["chapterThreeMorning", "届かない手紙", "scene"],
  ["chapterFourField", "冬のストリートピアノ", "scene"],
  ["chapterFourCafe", "終わらない夕暮れのカフェ", "scene"],
  ["chapterFourPiano", "ピアノとベンチ", "scene"],
  ["chapterFourAfternoon", "何も入っていない午後", "scene"],
  ["chapterFourRealizes", "額縁が見抜いたもの", "scene"],
  ["chapterFourHospital", "一瞬の朝", "scene"],
  ["chapterFiveField", "積み重なった書斎", "scene"],
  ["chapterFiveDining", "いつもの食卓", "scene"],
  ["chapterFiveStain", "取れない染み", "scene"],
  ["chapterFiveLayered", "重なった時間", "scene"],
  ["chapterFiveCups", "二つのカップ", "scene"],
  ["chapterFiveMorning", "長い休暇の朝", "scene"],
  ["chapterSixMusicRoom", "最初の音", "scene"],
  ["chapterSixRainyRoom", "雨の日の部屋", "scene"],
  ["chapterSixField", "弾けなかった舞台", "scene"],
  ["chapterSixPianoMorning", "親友の朝", "scene"],
  ["chapterSixWhatHappened", "二つの記憶", "scene"],
  ["chapterSixFreeze", "止まった演奏", "scene"],
  ["chapterSixFall", "《窓》への落下", "scene"],
  ["chapterSixWaiting", "待ってる", "scene"],
  ["chapterSixRescue", "劇場へ引き上げる手", "scene"],
  ["chapterSixSeventhNote", "七つ目の音", "scene"],
  ["chapterSixBackstage", "濡れた砂", "scene"],
  ["chapterSixFriendMorning", "親友の朝", "scene"],
  ["chapterSixCompass", "いつかの劇場", "scene"],
  ["chapterSixOuterWindow", "入れ子の《窓》", "scene", true],
]);

const CHAPTER_STORIES: Readonly<Record<string, ScenarioStoryDefinition>> = Object.freeze({
  "chapter-01": buildStory({
    chapterId: "chapter-01",
    script: chapterOneScript,
    openingScenes: ["第一場", "第二場"],
    battleScenes: ["第三場", "第四場"],
    resultScenes: ["第五場"],
    postscriptScenes: ["後日談"],
    sceneBackdrops: {
      第一場: "backstage",
      第二場: "theater",
      第三場: "loopingWalkStill",
      第四場: "ballReturnStill",
      第五場: "theater",
      後日談: "morningBedroom",
    },
    cues: [
      { pattern: /舞台の中央|円形の《窓》|黒い水面の下/, backdrop: "theater" },
      { pattern: /客席には人影|小さな目が瞬|《夜の目》の皆様/, backdrop: "nightEyes" },
      { pattern: /舞台袖から|壁にもたれた若い女性/, backdrop: "backstage" },
      { pattern: /主人公も黒い水へ手を入れる|二つの光が重なり/, backdrop: "windowActive" },
      { pattern: /赤い郵便受け|同じ街灯|帰れないこと/, backdrop: "dreamStreet" },
      { pattern: /ボールをくわえ|ちゃんと、帰ってくる/, backdrop: "ballReturnStill" },
      { pattern: /帰って、ごはん|家の明かり|並んで帰っていく/, backdrop: "walkingHomeStill" },
      { pattern: /舞台には、支配人だけ|黒い水面は、何も映さない/, backdrop: "theater" },
      { pattern: /舞台袖へ戻って|その背中を追う/, backdrop: "backstage" },
      { pattern: /犬は、その手へ鼻先/, backdrop: "morningStill" },
    ],
  }),
  "chapter-02": buildStory({
    chapterId: "chapter-02",
    script: chapterTwoScript,
    openingScenes: ["第一場", "第二場"],
    battleScenes: ["第三場", "第四場"],
    resultScenes: ["第五場"],
    postscriptScenes: ["後日談"],
    sceneBackdrops: {
      第一場: "theater",
      第二場: "chapterTwoMountain",
      第三場: "chapterTwoMountain",
      第四場: "chapterTwoSummit",
      第五場: "chapterTwoFlight",
      後日談: "chapterTwoMorning",
    },
    cues: [
      { pattern: /背中へ|空へ|飛び立|雲の上/, backdrop: "chapterTwoFlight" },
      { pattern: /朝|遠足の支度|いってきます/, backdrop: "chapterTwoMorning" },
    ],
  }),
  "chapter-03": buildStory({
    chapterId: "chapter-03",
    script: chapterThreeScript,
    openingScenes: ["第一場", "第二場"],
    battleScenes: ["第三場", "第四場"],
    resultScenes: ["第五場"],
    postscriptScenes: ["後日談"],
    sceneBackdrops: {
      第一場: "projectionRoom",
      第二場: "chapterThreeField",
      第三場: "chapterThreeField",
      第四場: "chapterThreeFireplace",
      第五場: "chapterThreeCocoa",
      後日談: "chapterThreeMorning",
    },
    cues: [
      { pattern: /廊下|黒い影|追ってくる/, backdrop: "chapterThreeNightmare" },
      { pattern: /雪の家|煙突|サンタ/, backdrop: "chapterThreeField" },
      { pattern: /暖炉|ココア|隣へ座る/, backdrop: "chapterThreeCocoa" },
      { pattern: /朝|手紙を書く/, backdrop: "chapterThreeMorning" },
    ],
  }),
  "chapter-04": buildStory({
    chapterId: "chapter-04",
    script: chapterFourScript,
    openingScenes: ["第一場", "第二場"],
    battleScenes: ["第三場", "第四場"],
    resultScenes: ["第五場", "第六場"],
    postscriptScenes: ["幕間"],
    sceneBackdrops: {
      第一場: "theater",
      第二場: "chapterFourPiano",
      第三場: "chapterFourField",
      第四場: "chapterFourField",
      第五場: "chapterFourAfternoon",
      第六場: "chapterFourRealizes",
      幕間: "chapterFourHospital",
    },
    cues: [
      { pattern: /冬の広場|ストリートピアノ|ベンチ/, backdrop: "chapterFourField" },
      { pattern: /買い物|カフェ|午後/, backdrop: "chapterFourAfternoon" },
      { pattern: /彼女って|君の……|見抜/, backdrop: "chapterFourRealizes" },
      { pattern: /病室|病院の天井|目を覚ます/, backdrop: "chapterFourHospital" },
    ],
  }),
  "chapter-05": buildStory({
    chapterId: "chapter-05",
    script: chapterFiveScript,
    openingScenes: ["第一場"],
    battleScenes: ["第二場", "第三場", "第四場"],
    resultScenes: ["第五場"],
    postscriptScenes: ["後日談"],
    sceneBackdrops: {
      第一場: "theater",
      第二場: "chapterFiveField",
      第三場: "chapterFiveStain",
      第四場: "chapterFiveLayered",
      第五場: "chapterFiveCups",
      後日談: "chapterFiveMorning",
    },
    cues: [
      { pattern: /染み|汚れ/, backdrop: "chapterFiveStain" },
      { pattern: /幼い|積み重|過去の部屋|時間が重な/, backdrop: "chapterFiveLayered" },
      { pattern: /二つのカップ|お茶を入れ|手を止め/, backdrop: "chapterFiveCups" },
      { pattern: /朝食|長い休暇|朝が/, backdrop: "chapterFiveMorning" },
    ],
  }),
  "chapter-06": buildStory({
    chapterId: "chapter-06",
    script: chapterSixScript,
    openingScenes: ["第一場", "第二場", "第三場"],
    battleScenes: ["第四場", "第五場", "第六場"],
    resultScenes: ["第七場", "第八場", "第九場"],
    postscriptScenes: ["後日談", "エンドロール"],
    sceneBackdrops: {
      第一場: "theater",
      第二場: "selectionTable",
      第三場: "chapterSixField",
      第四場: "chapterSixMusicRoom",
      第五場: "chapterSixWhatHappened",
      第六場: "chapterSixFreeze",
      第七場: "chapterSixWaiting",
      第八場: "chapterSixSeventhNote",
      第九場: "chapterSixBackstage",
      後日談: "chapterSixFriendMorning",
      エンドロール: "chapterSixPianoMorning",
    },
    cues: [
      { pattern: /音楽室|合唱の練習/, backdrop: "chapterSixMusicRoom" },
      { pattern: /雨の日|自室|小説を読/, backdrop: "chapterSixRainyRoom" },
      { pattern: /左が、起きたこと|二つの舞台|覚えていること/, backdrop: "chapterSixWhatHappened" },
      { pattern: /固まって|弾けなかった|指が鍵盤/, backdrop: "chapterSixFreeze" },
      { pattern: /《窓》へ落|水面へ|大きな落下音/, backdrop: "chapterSixFall" },
      { pattern: /待ってる/, backdrop: "chapterSixWaiting" },
      { pattern: /引き上げ|帰りの方角|捕まえた/, backdrop: "chapterSixRescue" },
      { pattern: /七つ目の音|次の一音/, backdrop: "chapterSixSeventhNote" },
      { pattern: /濡れた|舞台裏へ|砂時計/, backdrop: "chapterSixBackstage" },
      { pattern: /ピアノの前|親友の朝/, backdrop: "chapterSixFriendMorning" },
    ],
    selectedBranch: "《火打石》",
  }),
});

const extraOuterWindowStory = buildStory({
  chapterId: "extra-outer-window",
  script: extraOuterWindowScript,
  openingScenes: ["第一場", "第二場"],
  battleScenes: [],
  resultScenes: [],
  postscriptScenes: [],
  sceneBackdrops: {
    第一場: "chapterSixCompass",
    第二場: "chapterSixOuterWindow",
  },
  cues: [],
});

const extraOuterWindowTitle: StoryEntry = Object.freeze({
  kind: "title",
  eyebrow: "EXTRA",
  text: "窓の外の朝",
  backdrop: "chapterSixCompass",
});

export const EXTRA_OUTER_WINDOW = Object.freeze({
  id: "extra-outer-window",
  numberLabel: "おまけ",
  title: "窓の外の朝",
  description: "すべての幕が下りたあと、《窓》の外に残された短い一幕。",
  story: extraOuterWindowStory,
  sequence: Object.freeze([
    extraOuterWindowTitle,
    ...extraOuterWindowStory.opening,
  ]),
});

export function getStoryForChapter(chapterId: string): ScenarioStoryDefinition {
  return CHAPTER_STORIES[chapterId] ?? CHAPTER_ONE_STORY;
}

function buildStory(plan: ChapterStoryPlan): ScenarioStoryDefinition {
  const speakers: Record<string, StorySpeakerDefinition> = { ...BASE_SPEAKERS };
  const script = prepareScript(plan.script, plan.selectedBranch);
  const scenes = parseScenes(script, plan.sceneBackdrops, plan.cues, speakers);
  const opening = collectScenes(scenes, plan.openingScenes);
  const battle = collectScenes(scenes, plan.battleScenes);
  const resultCommon = collectScenes(scenes, plan.resultScenes);
  const postscriptCommon = collectScenes(scenes, plan.postscriptScenes);
  const afterTurn = splitAcrossTurns(battle, 6);
  const emptySequence: readonly StoryEntry[] = Object.freeze([]);
  const emptyResults: Readonly<Record<ScenarioResultKey, readonly StoryEntry[]>> = Object.freeze({
    ideal: emptySequence,
    oversleep: emptySequence,
    low: emptySequence,
    nightmare: emptySequence,
  });

  return Object.freeze({
    speakers: Object.freeze(speakers),
    backdrops: BACKDROPS,
    opening: Object.freeze(opening),
    beforeTurn: Object.freeze({}),
    afterTurn: Object.freeze(afterTurn),
    resonanceResult: Object.freeze({
      resonance: Object.freeze([]),
      split: Object.freeze([]),
    }),
    resultScenes: Object.freeze(emptyResults),
    resultCommon: Object.freeze(resultCommon),
    closing: Object.freeze([]),
    postscriptOpening: Object.freeze([]),
    postscriptScenes: Object.freeze({ ...emptyResults }),
    postscriptCommon: Object.freeze(postscriptCommon),
  });
}

function prepareScript(script: string, selectedBranch?: string): string {
  let prepared = script
    .replaceAll("、{主人公名}", "")
    .replaceAll("{主人公名}", "主人公")
    .replace("プレイヤーは六枚から一つを選ぶ。", "主人公は六枚を見比べ、《火打石》の札を選ぶ。");
  if (!selectedBranch) return prepared;

  const lines = prepared.split(/\r?\n/);
  const output: string[] = [];
  let includeBranch = true;
  for (const line of lines) {
    if (line.startsWith("## ")) includeBranch = true;
    if (line.startsWith("### ")) {
      const heading = line.slice(4).trim();
      const isBranchHeading = heading.includes("場合") || heading.includes("による救出");
      includeBranch = !isBranchHeading || heading.includes(selectedBranch) || heading === "共通";
      continue;
    }
    if (includeBranch) output.push(line);
  }
  prepared = output.join("\n");
  return prepared;
}

function parseScenes(
  script: string,
  sceneBackdrops: Readonly<Record<string, string>>,
  cues: readonly BackdropCue[],
  speakers: Record<string, StorySpeakerDefinition>,
): readonly ScriptScene[] {
  const sections = script.split(/^##\s+/m).slice(1);
  const scenes: ScriptScene[] = [];
  for (const section of sections) {
    const lines = section.split(/\r?\n/);
    const heading = lines.shift()?.trim() ?? "";
    if (!heading || heading === "登場人物" || heading.startsWith("この草稿で")) continue;
    const sceneKey = Object.keys(sceneBackdrops).find((key) => heading.startsWith(key));
    if (!sceneKey) continue;
    const baseBackdrop = sceneBackdrops[sceneKey] ?? "theater";
    scenes.push({
      heading,
      entries: Object.freeze(parseSceneEntries(
        heading,
        lines,
        baseBackdrop,
        cues,
        speakers,
      )),
    });
  }
  return Object.freeze(scenes);
}

function parseSceneEntries(
  heading: string,
  lines: readonly string[],
  baseBackdrop: string,
  cues: readonly BackdropCue[],
  speakers: Record<string, StorySpeakerDefinition>,
): StoryEntry[] {
  const [eyebrow, ...titleParts] = heading.split(/[　\s]+/);
  const entries: StoryEntry[] = [{
    kind: "title",
    eyebrow: eyebrow || "STORY",
    text: titleParts.join("　") || heading,
    backdrop: baseBackdrop,
    ...(heading.startsWith("エンドロール") ? { cue: "bgm:ending" } : {}),
  }];
  let activeBackdrop = baseBackdrop;
  let narrationParts: string[] = [];

  const applyBackdrop = (text: string): string | undefined => {
    const matched = cues.find((cue) => cue.pattern.test(text));
    if (!matched || matched.backdrop === activeBackdrop) return undefined;
    activeBackdrop = matched.backdrop;
    return activeBackdrop;
  };
  const flushNarration = (): void => {
    if (narrationParts.length === 0) return;
    const text = narrationParts.join("　");
    const backdrop = applyBackdrop(text);
    entries.push({
      kind: "line",
      speaker: "narrator",
      text,
      tone: "narration",
      ...(backdrop ? { backdrop } : {}),
    });
    narrationParts = [];
  };

  for (const rawLine of lines) {
    const line = cleanText(rawLine);
    if (!line || line === "---" || line.startsWith(">")) continue;
    if (line.startsWith("### ") || line.startsWith("- ")) continue;
    const dialogue = line.match(/^\*\*(.+?)\*\*\s*(.+)$/);
    if (dialogue) {
      flushNarration();
      const rawSpeaker = dialogue[1]?.trim() ?? "語り";
      const speakerId = resolveSpeakerId(rawSpeaker, speakers);
      const text = stripMarkdownEmphasis(stripJapaneseQuotes(dialogue[2]?.trim() ?? ""));
      const backdrop = applyBackdrop(text);
      entries.push({
        kind: "line",
        speaker: speakerId,
        text,
        ...(backdrop ? { backdrop } : {}),
      });
      continue;
    }
    if (line.startsWith("プレイヤーは")) continue;
    const narrationLine = stripMarkdownEmphasis(line);
    const projectedLength = narrationParts.join("　").length + narrationLine.length;
    if (projectedLength > 150) flushNarration();
    narrationParts.push(narrationLine);
  }
  flushNarration();
  return entries;
}

function collectScenes(
  scenes: readonly ScriptScene[],
  prefixes: readonly string[],
): StoryEntry[] {
  const entries: StoryEntry[] = [];
  for (const prefix of prefixes) {
    const scene = scenes.find((candidate) => candidate.heading.startsWith(prefix));
    if (scene) entries.push(...scene.entries);
  }
  return entries;
}

function splitAcrossTurns(
  entries: readonly StoryEntry[],
  turnCount: number,
): Readonly<Partial<Record<number, readonly StoryEntry[]>>> {
  const result: Partial<Record<number, readonly StoryEntry[]>> = {};
  let offset = 0;
  for (let turn = 1; turn <= turnCount; turn += 1) {
    const remainingTurns = turnCount - turn + 1;
    const remainingEntries = entries.length - offset;
    const size = Math.ceil(remainingEntries / remainingTurns);
    result[turn] = Object.freeze(entries.slice(offset, offset + size));
    offset += size;
  }
  return result;
}

function resolveSpeakerId(
  rawName: string,
  speakers: Record<string, StorySpeakerDefinition>,
): string {
  const normalized = rawName.replace(/の声$/, "").trim();
  const known = SPEAKER_ALIASES[normalized];
  if (known) return known;
  const id = "guest-" + stableSlug(normalized);
  if (!speakers[id]) {
    speakers[id] = speaker(id, normalized, "今宵の登場人物", normalized.slice(0, 1), "");
  }
  return id;
}

function speaker(
  id: string,
  name: string,
  role: string,
  symbol: string,
  portraitId: string,
): StorySpeakerDefinition {
  return Object.freeze({ id, name, role, symbol, portraitId });
}

function createBackdrops(
  definitions: readonly (readonly [
    string,
    string,
    StoryBackdropDefinition["mode"],
    boolean?,
  ])[],
): Readonly<Record<string, StoryBackdropDefinition>> {
  return Object.freeze(Object.fromEntries(definitions.map(([id, label, mode, hidePortraits]) => [
    id,
    Object.freeze({ id, label, imageId: id, mode, hidePortraits }),
  ])));
}

function cleanText(value: string): string {
  return value
    .trim()
    .replaceAll("`", "");
}

function stripMarkdownEmphasis(value: string): string {
  return value
    .replace(/\*\*([^*]+)\*\*/g, "$1")
    .replace(/\*([^*]+)\*/g, "$1");
}

function stripJapaneseQuotes(value: string): string {
  return value.startsWith("「") && value.endsWith("」")
    ? value.slice(1, -1)
    : value;
}

function stableSlug(value: string): string {
  let hash = 2166136261;
  for (const character of value) {
    hash ^= character.codePointAt(0) ?? 0;
    hash = Math.imul(hash, 16777619);
  }
  return (hash >>> 0).toString(36);
}
